import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Box, Typography, useTheme } from '@mui/material';
import type { GridColDef, GridPaginationModel } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '@verantos-dev/shared-components';

import { ActionsMenuCell } from './components/ActionMenuCell';
import { ProjectStatusCell } from './components/ProjectStatusCell';
import { ProjectSummaryCards } from './components/ProjectSummaryCards';
import { useManageProjectsDataGridFetch } from './hook/useDatagridManageProjectsFetch';
import { setProjectsListPaginationModel } from './state/adminProjectSlice';
import { selectProjectsListPage, selectProjectsListPageSize } from './state/selector';
import { ManageProjectResponse } from './types/response';
import { useAdminProjectsScreen } from './useAdminProjectsScreen';
import { DATAGRID_PAGE_SIZE_OPTIONS } from '../../common/constants';
import { DataGridToolbar } from '../../components/DatagridToolbar/DataGridToolbar';
import { getAnnotationToolAdminProjectsPath } from '../../lib/routePaths';
import { useAppDispatch, useAppSelector } from '../../lib/store';
import { DataGridLayout } from '../../wrapper-components/DataGrid/components/DataGridLayout/DataGridLayout';
import { DataGrid } from '../../wrapper-components/DataGrid/DataGrid';

export const AdminProjectsScreen = () => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const {
    draftProjectsCount,
    inProgressProjectsCount,
    completedProjectsCount,
    totalProjectsCount,
    referenceStandardReviewProjectsCount,
    navigateToCreateProject,
    isProjectsSummaryLoading,
  } = useAdminProjectsScreen();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const projectsListPage = useAppSelector(selectProjectsListPage);
  const projectsListPageSize = useAppSelector(selectProjectsListPageSize);

  const handlePaginationModelChange = (model: GridPaginationModel) => {
    dispatch(setProjectsListPaginationModel({ page: model.page, pageSize: model.pageSize }));
  };

  const { isFetching, data } = useManageProjectsDataGridFetch() || {};
  const prevRowCountRef = useRef<number | null>(null);

  useEffect(() => {
    if (!isFetching) {
      const currentRowCount = data?.data?.length ?? null;
      if (prevRowCountRef.current !== null && prevRowCountRef.current !== currentRowCount) {
        dispatch(setProjectsListPaginationModel({ page: 0, pageSize: projectsListPageSize }));
      }
      prevRowCountRef.current = currentRowCount;
    }
  }, [isFetching]);

  const [editingStatusProjectId, setEditingStatusProjectId] = useState<string | null>(null);
  const onChangeStatus = (projectId: string) => {
    setEditingStatusProjectId(projectId);
  };
  const clearEditMode = () => {
    setEditingStatusProjectId(null);
  };
  const onEditProject = (projectId: string) => {
    navigate(`${getAnnotationToolAdminProjectsPath()}/project-details/${projectId}?edit=1`);
  };

  const columns: GridColDef<ManageProjectResponse>[] = useMemo(
    () => [
      {
        field: 'projectName',
        headerName: 'Project name',
        minWidth: 210,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => {
          const { projectName, projectId } = params.row;
          return (
            <Link
              to={`${getAnnotationToolAdminProjectsPath()}/project-details/${projectId}`}
              style={{
                textDecoration: 'none',
                color: theme.palette.primary.main,
                fontWeight: 'bold',
              }}
            >
              {projectName}
            </Link>
          );
        },
      },
      {
        field: 'conditionAreaName',
        headerName: 'Condition area',
        minWidth: 180,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'healthSystemName',
        headerName: 'Research partner',
        minWidth: 155,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 220,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        valueGetter: (value: any, row: ManageProjectResponse) => {
          if (!row?.status) return '';
          return formatMessage({
            id: `adminProjects.content.table.status.${row.status.toLowerCase()}`,
          });
        },
        renderCell: (params) => {
          return (
            <ProjectStatusCell
              projectId={params.row.projectId}
              status={params.row.status}
              allowedTransitions={params.row.allowedStatusTransitions}
              editingStatusProjectId={editingStatusProjectId}
              setEditingStatusProjectId={setEditingStatusProjectId}
            />
          );
        },
      },
      {
        field: 'createdAt',
        headerName: 'Created',
        minWidth: 160,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        valueGetter: (value?: string | null) => {
          return value ? dayjs(value).format('MMM D, YYYY') : '-';
        },
      },
      {
        field: 'startDate',
        headerName: 'Started',
        minWidth: 160,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        valueGetter: (value?: string | null) => {
          return value ? dayjs(value).format('MMM D, YYYY') : '-';
        },
      },
      {
        field: 'completedDate',
        headerName: 'Completed',
        minWidth: 160,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        valueGetter: (value?: string | null) => {
          return value ? dayjs(value).format('MMM D, YYYY') : '-';
        },
      },
      {
        field: 'annotatorsCount',
        headerName: 'Annotators',
        minWidth: 120,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
      },
      {
        field: 'encounters',
        minWidth: 250,
        type: 'string',
        align: 'right',
        headerAlign: 'right',
        valueGetter: (value: any, row: ManageProjectResponse) => {
          if (!row) return '';
          return `${row.validationEncountersCount} / ${row.trainingEncountersCount} / ${row.testEncountersCount}`;
        },
        renderHeader: () => (
          <Box sx={{ lineHeight: 1.2, fontWeight: 'bold', textAlign: 'right' }}>
            Encounters
            <br />
            (Validation / Training / Test)
          </Box>
        ),
        renderCell: (params) => {
          const { trainingEncountersCount, testEncountersCount, validationEncountersCount } =
            params.row;
          return (
            <Box>
              {validationEncountersCount} / {trainingEncountersCount} / {testEncountersCount}
            </Box>
          );
        },
      },
      {
        field: 'action',
        headerName: 'Action',
        minWidth: 80,
        type: 'string',
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => {
          const { projectId } = params.row;
          return (
            <ActionsMenuCell
              projectId={projectId}
              onChangeStatus={onChangeStatus}
              onEditProject={onEditProject}
              clearEditMode={clearEditMode}
            />
          );
        },
      },
    ],
    [formatMessage, theme, editingStatusProjectId]
  );
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: theme.spacing(4),
        }}
      >
        <Typography variant="h4">{formatMessage({ id: 'adminProjects.header.title' })}</Typography>
        <Button
          startIcon={<AddCircleOutlineIcon />}
          id="create-project-button"
          displayLabel={formatMessage({ id: 'adminProjects.header.createProject.button' })}
          onClickHandler={navigateToCreateProject}
          sx={{
            textTransform: 'none',
            fontSize: '14px',
            backgroundColor: theme.palette.primary.main,
          }}
        />
      </Box>
      <Box>
        <ProjectSummaryCards
          draftProjectsCount={draftProjectsCount}
          inProgressProjectsCount={inProgressProjectsCount}
          completedProjectsCount={completedProjectsCount}
          totalProjectsCount={totalProjectsCount}
          referenceStandardReviewProjectsCount={referenceStandardReviewProjectsCount}
          isProjectsSummaryLoading={isProjectsSummaryLoading}
        />
      </Box>
      <Box sx={{ p: theme.spacing(4) }}>
        <DataGridLayout id="admin-projects-data-grid">
          <DataGrid
            id="admin-projects-data-grid"
            mode="client"
            pagination
            loadOnFetch
            pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
            paginationModel={{ page: projectsListPage, pageSize: projectsListPageSize }}
            onPaginationModelChange={handlePaginationModelChange}
            disableSearchParamSync
            preventUrlReadWhenSyncDisabled
            getRowId={(row) => row.projectId}
            columns={columns}
            useFetchDataGridData={useManageProjectsDataGridFetch}
            slots={{ toolbar: DataGridToolbar }}
            sx={{
              maxHeight: '70vh',
              '& .MuiDataGrid-row': {
                borderBottom: `1px solid ${theme.palette.divider}`,
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                whiteSpace: 'normal',
                lineHeight: '2rem',
              },
              '& .MuiDataGrid-columnHeader': {
                alignItems: 'flex-start',
              },
              '& .MuiDataGrid-cell--pinnedLeft': {
                borderRight: `2px solid ${theme.palette.divider}`,
              },
              '& .MuiDataGrid-columnHeader--pinnedLeft': {
                borderRight: `2px solid ${theme.palette.divider}`,
              },
            }}
            initialState={{
              pinnedColumns: {
                left: ['projectName'],
              },
            }}
          />
        </DataGridLayout>
      </Box>
    </Box>
  );
};
